import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  section?: string;
  tags?: string[];
  noindex?: boolean;
  nofollow?: boolean;
  // Rank Math specific fields
  facebookTitle?: string;
  facebookDescription?: string;
  facebookImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  twitterCardType?: 'summary' | 'summary_large_image';
  canonicalUrl?: string;
  schemaData?: any;
}

const defaultTitle = 'LawX - A melhor IA para advogados';
const defaultDescription = 'Otimize sua prática jurídica com a melhor IA para advogados. A inteligência artificial para advogado. Se concentre mais no que realmente importa. Descubra o verdadeiro poder jurídico ai';
const defaultImage = 'https://imagedelivery.net/xVIHcLAprA3z5a44SkpIlA/3c243362-9367-4613-ec3b-4eea68888c00/public';
const siteUrl = 'https://lawx.ai';

export default function SEO({
  title,
  description = defaultDescription,
  keywords,
  image = defaultImage,
  url,
  type = 'website',
  publishedTime,
  modifiedTime,
  author,
  section,
  tags,
  noindex = false,
  nofollow = false,
  facebookTitle,
  facebookDescription,
  facebookImage,
  twitterTitle,
  twitterDescription,
  twitterImage,
  twitterCardType = 'summary_large_image',
  canonicalUrl,
  schemaData,
}: SEOProps) {
  const fullTitle = title ? `${title} | LawX` : defaultTitle;
  const fullUrl = canonicalUrl || (url ? `${siteUrl}${url}` : siteUrl);
  const robotsContent = [
    noindex ? 'noindex' : 'index',
    nofollow ? 'nofollow' : 'follow',
  ].join(', ');

  // Usar títulos e descrições específicas do Rank Math ou fallback para padrão
  const ogTitle = facebookTitle || fullTitle;
  const ogDescription = facebookDescription || description;
  const ogImage = facebookImage || image;
  const twTitle = twitterTitle || fullTitle;
  const twDescription = twitterDescription || description;
  const twImage = twitterImage || image;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph / Facebook / LinkedIn / Instagram */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="LawX" />
      <meta property="og:locale" content="pt_BR" />

      {/* Article specific meta tags */}
      {type === 'article' && (
        <>
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          {author && <meta property="article:author" content={author} />}
          {section && <meta property="article:section" content={section} />}
          {tags && tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCardType} />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={twTitle} />
      <meta name="twitter:description" content={twDescription} />
      <meta name="twitter:image" content={twImage} />

      {/* JSON-LD Structured Data */}
      {type === 'article' && (
        <script type="application/ld+json">
          {JSON.stringify(
            schemaData || {
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: fullTitle,
              description: description,
              image: image,
              datePublished: publishedTime,
              dateModified: modifiedTime || publishedTime,
              author: {
                '@type': 'Organization',
                name: author || 'LawX',
              },
              publisher: {
                '@type': 'Organization',
                name: 'LawX',
                logo: {
                  '@type': 'ImageObject',
                  url: defaultImage,
                },
              },
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': fullUrl,
              },
              ...(tags && { keywords: tags.join(', ') }),
            }
          )}
        </script>
      )}

      {/* Website Schema (for non-article pages) */}
      {type === 'website' && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'LawX',
            url: siteUrl,
            description: description,
            potentialAction: {
              '@type': 'SearchAction',
              target: {
                '@type': 'EntryPoint',
                urlTemplate: `${siteUrl}/blog?search={search_term_string}`,
              },
              'query-input': 'required name=search_term_string',
            },
          })}
        </script>
      )}
    </Helmet>
  );
}

